import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Package, Plus, Edit2, Loader2, Eye, Trash2, History, Download, Archive, Building2, Star, X, Check, Hotel, CreditCard, Circle, LogIn } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminPageHeader from "@/components/AdminPageHeader";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import OpeningHoursEditor from "@/components/OpeningHoursEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Simplified Role System
const ROLES = {
  'admin': {
    label: 'Admin',
    description: 'Super administrator — full access to everything',
    color: 'bg-red-100 text-red-800 border-red-200'
  },
  'manager': {
    label: 'Staff',
    description: 'Internal staff — full access except audit logs, staff management, company info & integrations',
    color: 'bg-blue-100 text-blue-800 border-blue-200'
  },
  'customer': {
    label: 'Hotel',
    description: 'Hotel account — places and tracks their own orders',
    color: 'bg-gray-100 text-gray-800 border-gray-200'
  }
};

// Granular permission catalogue for Staff accounts. Each permission is a key
// that backend code (and the Sidebar) checks against `user.permissions`.
const PERMISSION_GROUPS = [
  {
    title: "Orders",
    permissions: [
      { id: "orders.view", label: "View orders (read-only)" },
      { id: "orders.edit", label: "Edit orders (items, qty, adjustments, delivery date)" },
      { id: "orders.cancel", label: "Cancel orders (and permanently delete canceled)" },
      { id: "orders.create_for_customer", label: "Create orders on behalf of a hotel" },
      { id: "orders.export", label: "Export & download invoices" },
    ],
  },
  {
    title: "Dispatch & Receiving",
    permissions: [
      { id: "dispatch.manage", label: "Picking & Dispatch" },
      { id: "receiving.manage", label: "Receiving (returns)" },
    ],
  },
  {
    title: "Catalogue & Hotels",
    permissions: [
      { id: "inventory.manage", label: "Inventory / linen items" },
      { id: "products.manage", label: "Product catalogue & pricing" },
      { id: "users.manage_hotels", label: "Edit Hotel users (add / edit / delete hotel accounts)" },
      { id: "users.manage_staff", label: "Edit Staff users (assign permissions & change passwords)" },
      { id: "routes.manage", label: "Delivery routes" },
    ],
  },
  {
    title: "Visibility",
    permissions: [
      { id: "audit.view", label: "View audit logs" },
      { id: "archived.view", label: "View archived data" },
      { id: "dashboard.view", label: "Dashboard & analytics" },
      { id: "announcements.manage", label: "Announcements" },
      { id: "company.manage", label: "Company info & settings" },
    ],
  },
];
const ALL_PERMISSION_IDS = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.id));

// Role presets — permissions are no longer hand-picked. Staff gets everything
// EXCEPT: audit logs, managing non-hotel users (staff/drivers/admins), and
// company info. (Integrations is hard-gated to super admin elsewhere.)
const STAFF_EXCLUDED_PERMS = ["audit.view", "users.manage_staff", "company.manage"];
const STAFF_PRESET_PERMS = ALL_PERMISSION_IDS.filter((id) => !STAFF_EXCLUDED_PERMS.includes(id));

export default function UsersTab({ users, routes, products, fetchAllData }) {
  // Get current user info
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentUserRole = currentUser.role || "customer";
  const currentUsername = currentUser.username || "";
  const isSuperAdmin = currentUserRole === "admin";

  const handleLoginAs = async (user) => {
    try {
      const r = await axios.post(`${API}/admin/impersonate/${user.id}`, {}, { headers: getAuthHeader() });
      localStorage.setItem("impersonate_id", r.data.id);
      localStorage.setItem("impersonate_name", r.data.hotel_name || r.data.username || r.data.email || "customer");
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log in as this user");
    }
  };
  const currentPerms = Array.isArray(currentUser.permissions) ? currentUser.permissions : [];
  const canManageHotelUsers = isSuperAdmin || currentPerms.includes("users.manage_hotels");
  const canManageStaffUsers = isSuperAdmin || currentPerms.includes("users.manage_staff");

  // Helper function to check if current user can edit a target user.
  // The catalogue exposes TWO separate perms:
  //   • users.manage_hotels — edit / create / delete hotel (customer) users
  //   • users.manage_staff  — edit Staff (role=manager) users
  // Only an admin-role account can ever touch another admin.
  const canEditUser = (targetUser) => {
    const targetRole = targetUser.role || "customer";

    if (isSuperAdmin) return true;
    if (targetRole === "admin") return false;       // only super admin can touch admins
    if (targetRole === "manager") return canManageStaffUsers;
    if (targetRole === "driver") return canManageStaffUsers;  // drivers are staff-side
    if (targetRole === "customer") return canManageHotelUsers;
    return false;
  };
  
  // Default role for a new user. If the operator only has the staff-mgmt
  // perm (no hotel-mgmt), default to "manager" — otherwise prefer "customer".
  // Super-admin always gets "customer" as the friendly default.
  const defaultNewUserRole = (!canManageHotelUsers && canManageStaffUsers) ? "manager" : "customer";

  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showProducts, setShowProducts] = useState(false);
  // When products are being edited for a specific hotel (vs the user-level
  // default), this holds the hotel record so we can scope the API call and
  // show the right title.
  const [productsHotel, setProductsHotel] = useState(null);
  const [showUserOrders, setShowUserOrders] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [archiveDialog, setArchiveDialog] = useState({ open: false, user: null });
  const [hotelsDialog, setHotelsDialog] = useState({ open: false, user: null });
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    hotel_name: "",
    hotel_code: "",
    hotel_address: "",
    suburb: "",
    route_id: "none",
    role: "customer",
  });
  const [userProducts, setUserProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userFilter, setUserFilter] = useState("all"); // "all", "customer", "manager", "admin"

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        email: formData.email,
        username: formData.username && formData.username.trim() ? formData.username.trim() : undefined,
        password: formData.password,
        hotel_name: formData.hotel_name,
        hotel_code: formData.hotel_code || null,
        hotel_address: formData.hotel_address || "",
        suburb: formData.suburb || "",
        route_id: formData.route_id === "none" ? null : formData.route_id,
        role: formData.role,
        is_admin: formData.role === "admin" || formData.role === "manager",
        // Role presets — Staff always gets the fixed preset, no manual picking.
        permissions: formData.role === "manager" ? STAFF_PRESET_PERMS : [],
      };

      if (editingUser) {
        // For updates, only send fields that can be updated
        const updatePayload = {
          username: formData.username && formData.username.trim() ? formData.username.trim() : undefined,          hotel_name: formData.hotel_name,
          hotel_code: formData.hotel_code || null,
          hotel_address: formData.hotel_address || "",
          suburb: formData.suburb || "",
          route_id: payload.route_id,
          role: formData.role,
          // Role presets applied on EVERY save so legacy hand-picked
          // permission sets converge to the standard preset.
          permissions: formData.role === "manager" ? STAFF_PRESET_PERMS : [],
        };
        
        // Only send password if it was changed
        if (formData.password && formData.password.trim()) {
          updatePayload.password = formData.password;
        }

        // Only send email if it actually changed — backend syncs Supabase Auth.
        const newEmail = (formData.email || "").trim().toLowerCase();
        if (newEmail && newEmail !== (editingUser.email || "").trim().toLowerCase()) {
          updatePayload.email = newEmail;
        }
        
        await axios.put(`${API}/users/${editingUser.id}`, updatePayload, { headers: getAuthHeader() });
        // If the user just renamed themselves, refresh localStorage so the
        // display name in the sidebar / dropdown updates without needing a
        // full re-login. Permission checks still use `role`, which is
        // untouched.
        try {
          const stored = JSON.parse(localStorage.getItem("user") || "{}");
          if (stored && stored.id === editingUser.id && updatePayload.username) {
            stored.username = updatePayload.username;
            localStorage.setItem("user", JSON.stringify(stored));
          }
        } catch (_e) { /* ignore */ }
        toast.success("User updated");
      } else {
        const resp = await axios.post(`${API}/auth/register`, payload, { headers: getAuthHeader() });
        toast.success(`User created — login email: ${resp.data.email}`);
      }

      setOpen(false);
      setEditingUser(null);
      setFormData({ email: "", username: "", password: "", hotel_name: "", hotel_code: "", hotel_address: "", suburb: "", route_id: "none", role: defaultNewUserRole });
      fetchAllData();
    } catch (error) {
      console.error("User operation error:", error);
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email || "",
      username: user.username || "",
      // Always start blank when editing — `user.password` from the API is
      // undefined, which makes the input UNCONTROLLED. Browsers then happily
      // autofill the currently-logged-in admin's saved credentials into this
      // field, and on save we'd overwrite the target user's password with
      // the admin's. (Symptom: "Why does login keep saying my password is
      // wrong — I never changed it!"). The placeholder makes intent obvious.
      password: "",
      hotel_name: user.hotel_name,
      hotel_code: user.hotel_code || "",
      hotel_address: user.hotel_address || "",
      suburb: user.suburb || "",
      route_id: user.route_id || "none",
      role: user.role || "customer",
    });
    setOpen(true);
  };

  const openProductsDialog = async (user, hotel = null) => {
    setEditingUser(user);
    setProductsHotel(hotel);
    try {
      const qs = hotel?.id ? `?hotel_id=${encodeURIComponent(hotel.id)}` : "";
      const response = await axios.get(`${API}/users/${user.id}/products${qs}`, { headers: getAuthHeader() });
      const productsArray = (response.data.products || []).map((p, index) => ({
        product_id: p.product_id,
        price: p.price,
        sort_order: p.sort_order !== undefined ? p.sort_order : index
      }));
      setUserProducts(productsArray);
      setShowProducts(true);
    } catch (error) {
      toast.error("Failed to load user products");
    }
  };

  const handleProductToggle = (productId) => {
    setUserProducts(prev => {
      const exists = prev.find(p => p.product_id === productId);
      if (exists) {
        // 移除产品
        return prev.filter(p => p.product_id !== productId);
      } else {
        // 添加产品
        return [...prev, { product_id: productId, price: 0, sort_order: prev.length }];
      }
    });
  };

  const handlePriceChange = (productId, price) => {
    setUserProducts(prev => prev.map(p => 
      p.product_id === productId ? { ...p, price: parseFloat(price) || 0 } : p
    ));
  };

  const moveProductUp = (index) => {
    if (index === 0) return;
    setUserProducts(prev => {
      const newProducts = [...prev];
      [newProducts[index - 1], newProducts[index]] = [newProducts[index], newProducts[index - 1]];
      // 更新 sort_order
      return newProducts.map((p, i) => ({ ...p, sort_order: i }));
    });
  };

  const moveProductDown = (index) => {
    setUserProducts(prev => {
      if (index === prev.length - 1) return prev;
      const newProducts = [...prev];
      [newProducts[index], newProducts[index + 1]] = [newProducts[index + 1], newProducts[index]];
      // 更新 sort_order
      return newProducts.map((p, i) => ({ ...p, sort_order: i }));
    });
  };

  const saveUserProducts = async () => {
    setLoading(true);
    try {
      const productsArray = userProducts.map((p, index) => ({
        product_id: p.product_id,
        price: parseFloat(p.price) || 0,
        sort_order: index
      }));

      const qs = productsHotel?.id ? `?hotel_id=${encodeURIComponent(productsHotel.id)}` : "";
      await axios.put(
        `${API}/users/${editingUser.id}/products${qs}`,
        { products: productsArray },
        { headers: getAuthHeader() }
      );
      toast.success(
        productsHotel?.hotel_name
          ? `Products updated for ${productsHotel.hotel_name}`
          : "Default products updated"
      );
      setShowProducts(false);
      setProductsHotel(null);
      setEditingUser(null);
    } catch (error) {
      toast.error("Failed to update user products");
    } finally {
      setLoading(false);
    }
  };


  const openUserOrdersDialog = async (user) => {
    setEditingUser(user);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/${user.id}/orders`, {
        headers: getAuthHeader()
      });
      setUserOrders(response.data.orders || []);
      setShowUserOrders(true);
    } catch (error) {
      toast.error("Failed to load user orders");
    } finally {
      setLoading(false);
    }
  };

  const handleExportUserOrders = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/orders/export`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Orders exported successfully");
    } catch (error) {
      toast.error("Failed to export orders");
    }
  };

  const handleArchiveUser = async () => {
    if (!archiveDialog.user) return;

    setLoading(true);
    try {
      await axios.put(`${API}/users/${archiveDialog.user.id}/archive?archive=true`, 
        {},
        { headers: getAuthHeader() }
      );
      toast.success("User archived successfully");
      setArchiveDialog({ open: false, user: null });
      fetchAllData();
    } catch (error) {
      toast.error("Failed to archive user");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    // Prevent deletion of any admin-role account
    if (deleteDialog.user.role === 'admin') {
      toast.error("Cannot delete admin accounts");
      setDeleteDialog({ open: false, user: null });
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/users/${deleteDialog.user.id}`, {
        headers: getAuthHeader()
      });
      toast.success("User and all data deleted successfully");
      setDeleteDialog({ open: false, user: null });
      fetchAllData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete user";
      toast.error(errorMsg);
      setDeleteDialog({ open: false, user: null });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow-lg border-0">
      <CardHeader>
        <div className="space-y-4">
          <AdminPageHeader
            title="Users"
            description="Manage customer accounts and their products"
            actions={
              <>
              {(canManageHotelUsers || canManageStaffUsers) && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-user-button"
                    onClick={() => {
                      setEditingUser(null);
                      setFormData({ email: "", username: "", password: "", hotel_name: "", hotel_code: "", hotel_address: "", suburb: "", route_id: "none", role: defaultNewUserRole });
                    }}
                    className="bg-brand-navy hover:bg-brand-navy-hover text-white rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
                <DialogDescription>
                  {editingUser
                    ? "Update account credentials and role. Hotel allocation is managed via the 🏨 Hotels button on the user row."
                    : "Create a login account. After creation, allocate one or more hotels to this user via the 🏨 Hotels button."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email (for login)</Label>
                  <Input
                    id="user-email"
                    data-testid="user-email-input"
                    type="email"
                    placeholder="hotel@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                  {editingUser && (
                    <p className="text-xs text-gray-500">Changing this updates the login email immediately — the user signs in with the new address right away</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-username">Username</Label>
                  <Input
                    id="user-username"
                    data-testid="user-username-input"
                    type="text"
                    placeholder="e.g. john_smith"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Display name shown across the admin app. Leave blank to derive it from the email prefix.
                  </p>
                </div>
                {/* Password field - always show for creating/editing users you can manage */}
                <div className="space-y-2">
                  <Label htmlFor="user-password">Password</Label>
                  <Input
                    id="user-password"
                    data-testid="user-password-input"
                    type="text"
                    value={formData.password || ""}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    placeholder={editingUser ? "Leave blank to keep current password" : "Set a password"}
                    // `new-password` tells password managers NOT to autofill
                    // the admin's own saved credentials into this field —
                    // otherwise editing any user silently rotates their
                    // password to the admin's.
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {editingUser && (
                    <p className="text-xs text-gray-500">Type a new password to change it, or leave blank to keep the current one.</p>
                  )}
                </div>

                {/* Role Selection - Simplified Three-Tier System */}
                <div className="space-y-3 border-t pt-4">
                  <Label htmlFor="user-role">Account Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Only show "Hotel" role to users with the hotel-mgmt perm. */}
                      {canManageHotelUsers && (
                      <SelectItem value="customer">
                        <div className="flex flex-col items-start">
                          <span className="font-medium">Hotel</span>
                          <span className="text-xs text-gray-500">Places & tracks their own orders</span>
                        </div>
                      </SelectItem>
                      )}
                      {/* "Staff" role requires the staff-mgmt perm — having
                          only `users.manage_hotels` is not enough to spawn
                          another Staff member. */}
                      {canManageStaffUsers && (
                      <SelectItem value="manager">
                        <div className="flex flex-col items-start">
                          <span className="font-medium">Staff</span>
                          <span className="text-xs text-gray-500">Standard staff access — no manual permission picking</span>
                        </div>
                      </SelectItem>
                      )}
                      {/* "Driver" is a PDA-only role — locked to the mobile
                          scanner app, no admin dashboard access. Uses the
                          same staff-mgmt perm to create/edit. */}
                      {canManageStaffUsers && (
                      <SelectItem value="driver">
                        <div className="flex flex-col items-start">
                          <span className="font-medium">Driver</span>
                          <span className="text-xs text-gray-500">PDA app only — no full-mode access</span>
                        </div>
                      </SelectItem>
                      )}
                      {/* Only the super-admin can mint other admin accounts.
                          Backend also enforces this (see /auth/register and
                          PUT /users/:id) — hiding the option here keeps the
                          UI consistent and avoids "submit then 403" surprise. */}
                      {isSuperAdmin && (
                      <SelectItem value="admin">
                        <div className="flex flex-col items-start">
                          <span className="font-medium">Admin</span>
                          <span className="text-xs text-gray-500">Super administrator — full access</span>
                        </div>
                      </SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  {/* Role-aware capability panel */}
                  {formData.role === "admin" && (
                    <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={ROLES.admin.color}>Admin</Badge>
                        <span className="text-sm text-red-700 font-medium">Full access — every feature, no restrictions.</span>
                      </div>
                    </div>
                  )}

                  {formData.role === "customer" && (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={ROLES.customer.color}>Hotel</Badge>
                        <span className="text-sm text-gray-700">Places & tracks their own orders. No admin tools.</span>
                      </div>
                    </div>
                  )}

                  {formData.role === "manager" && (
                    <div className="border rounded-lg p-4 bg-blue-50 border-blue-200 space-y-2" data-testid="staff-preset-panel">
                      <div className="flex items-center gap-2">
                        <Badge className={ROLES.manager.color}>Staff</Badge>
                        <span className="text-sm text-blue-800 font-medium">Standard staff access — applied automatically.</span>
                      </div>
                      <div className="text-sm text-blue-900 space-y-1">
                        <p>✓ Orders, dispatch, receiving, inventory, products, routes, hotel accounts, dashboard, archived data, announcements</p>
                        <p className="text-blue-700">✗ No audit logs · No editing staff/driver/admin accounts · No company info · No integrations</p>
                      </div>
                    </div>
                  )}

                  {formData.role === "driver" && (
                    <div className="border rounded-lg p-4 bg-amber-50 border-amber-200" data-testid="driver-preset-panel">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">Driver</Badge>
                        <span className="text-sm text-amber-800">PDA scanner app only — dispatch runs, deliveries, receiving & scanning. No admin dashboard.</span>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  data-testid="submit-user-button"
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingUser ? (
                    "Update User"
                  ) : (
                    "Create User"
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}

          <Dialog open={showProducts} onOpenChange={setShowProducts}>
            <DialogContent className="max-w-3xl flex flex-col max-h-[80vh]">
              <DialogHeader>
                <DialogTitle>
                  {productsHotel
                    ? `Manage Products — ${productsHotel.hotel_name}`
                    : `Default Products — ${editingUser?.username || editingUser?.email || ""}`}
                </DialogTitle>
                <DialogDescription>
                  {productsHotel
                    ? `Products and pricing for this specific hotel. Overrides the user's default catalogue.`
                    : `Catalogue used when a hotel does not have its own product list. Hotel-specific overrides are managed from the Hotels dialog.`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 overflow-y-auto flex-1 pr-2">
                {/* 已选择的产品 - 可排序 */}
                {userProducts.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm">Selected Products (drag to reorder)</h3>
                    {userProducts.map((userProd, index) => {
                      const product = products.find(p => p.id === userProd.product_id);
                      if (!product) return null;
                      return (
                        <div key={userProd.product_id} className="flex items-center space-x-2 p-3 border rounded-lg bg-blue-50">
                          <div className="flex flex-col space-y-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => moveProductUp(index)}
                              disabled={index === 0}
                            >
                              ▲
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0"
                              onClick={() => moveProductDown(index)}
                              disabled={index === userProducts.length - 1}
                            >
                              ▼
                            </Button>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-gray-500">Min. qty: {product.minimum_quantity} | Order: #{index + 1}</p>
                          </div>
                          <div className="w-32">
                            <Label className="text-xs">Price ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={userProd.price}
                              onChange={(e) => handlePriceChange(userProd.product_id, e.target.value)}
                              data-testid={`product-price-${userProd.product_id}`}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleProductToggle(userProd.product_id)}
                          >
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 可添加的产品 */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">Available Products</h3>
                  {products.filter(p => !userProducts.find(up => up.product_id === p.id)).map((product) => (
                    <div key={product.id} className="flex items-center space-x-4 p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-gray-500">Min. qty: {product.minimum_quantity}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleProductToggle(product.id)}
                        data-testid={`product-add-${product.id}`}
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t mt-4">
                <Button
                  onClick={saveUserProducts}
                  disabled={loading}
                  className="w-full"
                  data-testid="save-products-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Products"
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

              </>
            }
          />
        
        {/* User Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={userFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setUserFilter("all")}
          >
            All Users ({users.length})
          </Button>
          <Button
            variant={userFilter === "customer" ? "default" : "outline"}
            size="sm"
            onClick={() => setUserFilter("customer")}
          >
            Customers ({users.filter(u => u.role === "customer").length})
          </Button>
          <Button
            variant={userFilter === "manager" ? "default" : "outline"}
            size="sm"
            onClick={() => setUserFilter("manager")}
          >
            Managers ({users.filter(u => u.role === "manager").length})
          </Button>
          <Button
            variant={userFilter === "admin" ? "default" : "outline"}
            size="sm"
            onClick={() => setUserFilter("admin")}
          >
            Admins ({users.filter(u => u.role === "admin").length})
          </Button>
        </div>
      </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {users.filter(user => {
            if (userFilter === "all") return true;
            return user.role === userFilter;
          }).map((user) => (
            <Card key={user.id} data-testid={`user-card-${user.id}`} className="border-2">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                      <p className="font-semibold text-base sm:text-lg break-all">
                        {user.username || user.email}
                      </p>
                      {/* Hotel chips — show each hotel directly so admins can see allocations at a glance */}
                      {(user.hotels || []).length > 0 ? (
                        <>
                          {(user.hotels || []).slice(0, 6).map((h) => (
                            <Badge
                              key={h.id}
                              className="bg-amber-50 text-amber-900 border border-amber-200 font-medium text-[11px] sm:text-xs"
                              data-testid={`user-hotel-chip-${user.id}-${h.id}`}
                              title={h.hotel_code ? `${h.hotel_name} · ${h.hotel_code}` : h.hotel_name}
                            >
                              <Hotel className="w-3 h-3 mr-1" />
                              {h.hotel_name}{h.is_primary ? " ★" : ""}
                            </Badge>
                          ))}
                          {(user.hotels || []).length > 6 && (
                            <Badge className="bg-amber-50 text-amber-900 border border-amber-200 text-[11px] sm:text-xs">
                              +{user.hotels.length - 6} more
                            </Badge>
                          )}
                        </>
                      ) : (
                        <Badge
                          className="bg-gray-100 text-gray-600 border border-gray-200 text-[11px] sm:text-xs"
                          data-testid={`user-no-hotel-${user.id}`}
                        >
                          <Hotel className="w-3 h-3 mr-1" />
                          No hotel
                        </Badge>
                      )}
                    </div>
                    <p
                      className="text-xs sm:text-sm text-gray-700 break-all"
                      data-testid={`user-email-${user.id}`}
                    >
                      {user.email || `@${user.username}`}
                    </p>
                    <p className="text-[10px] sm:text-xs text-gray-400 font-mono truncate">ID: {user.id}</p>
                    {/* Show password if visible and not masked by backend */}
                    {user.password && user.password !== "********" && (
                      <div className="flex items-center gap-2 mt-2 min-w-0">
                        <Eye className="w-4 h-4 text-gray-400 shrink-0" />
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Password: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{user.password}</span></p>
                      </div>
                    )}
                    {/* Admin / Manager role badge — only shown for non-customer accounts so the
                        customer rows stay clean per UX request. */}
                    {user.role && user.role !== "customer" && ROLES[user.role] && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Badge className={ROLES[user.role].color}>
                          {ROLES[user.role].label}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 sm:shrink-0">
                    {/* Super admin: open the customer client as this user */}
                    {isSuperAdmin && (!user.role || user.role === "customer") && (
                      <Button
                        onClick={() => handleLoginAs(user)}
                        data-testid={`login-as-${user.id}`}
                        variant="outline"
                        size="sm"
                        className="h-9 text-amber-700 border-amber-300 hover:bg-amber-50"
                        title="Open the customer portal as this user"
                      >
                        <LogIn className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Login as</span>
                      </Button>
                    )}
                    {/* Orders button - everyone can view */}
                    <Button
                      onClick={() => openUserOrdersDialog(user)}
                      variant="outline"
                      size="sm"
                      className="h-9"
                    >
                      <History className="w-4 h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Orders</span>
                    </Button>

                    {/* Hotels, Edit, Archive, Delete - only if can edit.
                        Product allocation moved to per-hotel (inside Hotels dialog). */}
                    {canEditUser(user) && (
                      <>
                        <Button
                          onClick={() => setHotelsDialog({ open: true, user })}
                          data-testid={`manage-hotels-${user.id}`}
                          variant="outline"
                          size="sm"
                          className="h-9"
                        >
                          <Building2 className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Hotels</span>
                        </Button>
                        <Button
                          onClick={() => openEditDialog(user)}
                          data-testid={`edit-user-${user.id}`}
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => setArchiveDialog({ open: true, user })}
                          variant="outline"
                          size="icon"
                          className="text-orange-600 border-orange-300 hover:bg-orange-50 h-9 w-9"
                          title="Archive user"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                        {/* Hide delete button for admin-role users to prevent accidental deletion */}
                        {user.role !== 'admin' && (
                          <Button
                            onClick={() => setDeleteDialog({ open: true, user })}
                            variant="destructive"
                            size="icon"
                            className="h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>

      {/* User Orders Dialog */}
      <Dialog open={showUserOrders} onOpenChange={setShowUserOrders}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Order History - {editingUser?.hotel_name}</DialogTitle>
            <DialogDescription>
              View and export all orders for this hotel
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3">
            {userOrders.length === 0 ? (
              <div className="text-center py-12">
                <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No orders found</p>
              </div>
            ) : (
              userOrders.map((order) => (
                <Card key={order.id} className="border">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold">Order #{order.order_number}</p>
                        <p className="text-sm text-gray-600">
                          Delivery: {new Date(order.delivery_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={
                        order.status === "dispatched" ? "bg-green-100 text-green-800" :
                        order.status === "approved" ? "bg-blue-100 text-blue-800" :
                        "bg-yellow-100 text-yellow-800"
                      }>
                        {order.status}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="text-sm text-gray-600">
                          {item.product_name} × {item.quantity}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-sm font-semibold">Total: ${order.total_price.toFixed(2)}</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          <div className="pt-4 border-t flex gap-2">
            <Button
              onClick={() => handleExportUserOrders(editingUser?.id)}
              disabled={userOrders.length === 0}
              className="flex-1"
            >
              <Download className="w-4 h-4 mr-2" />
              Export All Orders
            </Button>
            <Button variant="outline" onClick={() => setShowUserOrders(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User - {deleteDialog.user?.hotel_name}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="text-red-600 font-semibold">
                ⚠️ This action cannot be undone!
              </p>
              <p>
                This will permanently delete:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>User account</li>
                <li>All orders (past and future)</li>
                <li>All product assignments</li>
              </ul>
              <p className="font-semibold text-orange-600">
                Have you exported all orders for this hotel?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, user: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Delete Everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive User Confirmation */}
      <AlertDialog open={archiveDialog.open} onOpenChange={(open) => setArchiveDialog({ open, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive User - {archiveDialog.user?.hotel_name}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Archiving this user will:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Remove them from the active user list</li>
                <li>Preserve all their order history</li>
                <li>Keep all audit logs intact</li>
                <li>Allow you to unarchive them later if needed</li>
              </ul>
              <p className="text-blue-600 font-semibold">
                ℹ️ This action can be reversed from the "Archived" tab
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setArchiveDialog({ open: false, user: null })}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveUser}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Archive User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hotels dialog — manage multiple hotels per user */}
      <HotelsDialog
        open={hotelsDialog.open}
        user={hotelsDialog.user}
        routes={routes}
        onClose={() => setHotelsDialog({ open: false, user: null })}
        onChanged={fetchAllData}
        onManageHotelProducts={(hotel) => {
          setHotelsDialog({ open: false, user: null });
          openProductsDialog(hotelsDialog.user, hotel);
        }}
      />
    </Card>
  );
}

// ============================================================================
// HotelsDialog — manage multiple hotels for a single user.
// ============================================================================
function HotelsDialog({ open, user, routes, onClose, onChanged, onManageHotelProducts }) {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newHotel, setNewHotel] = useState({ hotel_name: "", hotel_code: "", hotel_address: "", abn: "", suburb: "", route_id: "none", lat: null, lng: null, place_id: "", opening_hours: null, special_note: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editBase, setEditBase] = useState(null);
  const [openAsUser, setOpenAsUser] = useState(null);

  const load = async (uid) => {
    if (!uid) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/${uid}/hotels`, { headers: { ...getAuthHeader(), "x-no-cache": "1" } });
      setHotels(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load hotels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && user?.id) {
      setEditingId(null);
      setEditForm(null);
      setAdding(false);
      setNewHotel({ hotel_name: "", hotel_code: "", hotel_address: "", abn: "", suburb: "", route_id: "none", lat: null, lng: null, place_id: "", opening_hours: null, special_note: "" });
      load(user.id);
    }
  }, [open, user?.id]);

  const handleAdd = async () => {
    if (!newHotel.hotel_name.trim()) {
      toast.error("Hotel name is required");
      return;
    }
    try {
      const payload = {
        hotel_name: newHotel.hotel_name.trim(),
        hotel_code: newHotel.hotel_code.trim(),
        hotel_address: newHotel.hotel_address.trim(),
        abn: newHotel.abn.trim(),
        suburb: newHotel.suburb.trim(),
        route_id: newHotel.route_id === "none" ? null : newHotel.route_id,
        lat: newHotel.lat ?? null,
        lng: newHotel.lng ?? null,
        place_id: newHotel.place_id || null,
        opening_hours: newHotel.opening_hours,
      };
      await axios.post(`${API}/users/${user.id}/hotels`, payload, { headers: getAuthHeader() });
      toast.success("Hotel added");
      setAdding(false);
      setNewHotel({ hotel_name: "", hotel_code: "", hotel_address: "", abn: "", suburb: "", route_id: "none", lat: null, lng: null, place_id: "", opening_hours: null });
      await load(user.id);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add hotel");
    }
  };

  const startEdit = async (h) => {
    // Always edit against FRESH server data — a stale tab on another computer
    // must never overwrite newer changes with its old snapshot.
    let fresh = h;
    try {
      const res = await axios.get(`${API}/users/${user.id}/hotels`, { headers: { ...getAuthHeader(), "x-no-cache": "1" } });
      setHotels(res.data || []);
      fresh = (res.data || []).find((x) => x.id === h.id) || h;
    } catch { /* offline — fall back to the row we have */ }
    const base = {
      hotel_name: fresh.hotel_name,
      hotel_code: fresh.hotel_code,
      hotel_address: fresh.hotel_address,
      abn: fresh.abn || "",
      suburb: fresh.suburb,
      route_id: fresh.route_id || "none",
      lat: fresh.lat ?? null,
      lng: fresh.lng ?? null,
      place_id: fresh.place_id || "",
      opening_hours: fresh.opening_hours || null,
      special_note: fresh.special_note || "",
    };
    setEditBase(base);
    setEditForm({ ...base });
    setEditingId(h.id);
  };

  const saveEdit = async (h) => {
    try {
      const normalize = (f) => ({
        hotel_name: (f.hotel_name || "").trim(),
        hotel_code: (f.hotel_code || "").trim(),
        hotel_address: (f.hotel_address || "").trim(),
        abn: (f.abn || "").trim(),
        suburb: (f.suburb || "").trim(),
        route_id: f.route_id === "none" ? null : (f.route_id || null),
        lat: f.lat ?? null,
        lng: f.lng ?? null,
        place_id: f.place_id || null,
        opening_hours: f.opening_hours ?? null,
        special_note: (f.special_note || "").trim(),
      });
      const next = normalize(editForm);
      const base = normalize(editBase || {});
      // PATCH only the fields the user actually changed — untouched fields
      // keep whatever is currently on the server.
      const payload = {};
      for (const k of Object.keys(next)) {
        if (JSON.stringify(next[k] ?? null) !== JSON.stringify(base[k] ?? null)) payload[k] = next[k];
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setEditingId(null);
        setEditForm(null);
        setEditBase(null);
        return;
      }
      await axios.patch(`${API}/hotels/${h.id}`, payload, { headers: getAuthHeader() });
      toast.success("Saved");
      setEditingId(null);
      setEditForm(null);
      setEditBase(null);
      await load(user.id);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const setPrimary = async (h) => {
    try {
      await axios.post(`${API}/hotels/${h.id}/set-primary`, {}, { headers: getAuthHeader() });
      toast.success(`'${h.hotel_name}' set as primary`);
      await load(user.id);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete hotel '${h.hotel_name}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/hotels/${h.id}`, { headers: getAuthHeader() });
      toast.success("Hotel deleted");
      await load(user.id);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const setPaymentMode = async (h, mode) => {
    try {
      await axios.patch(`${API}/hotels/${h.id}`, { payment_mode: mode }, { headers: getAuthHeader() });
      toast.success(`Billing mode: ${mode === "manual_invoice" ? "Manual Invoice — card charging blocked" : "Direct Debit"}`);
      await load(user.id);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update billing mode");
    }
  };

  const routeName = (rid) => routes.find((r) => r.id === rid)?.name || "—";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hotels-dialog">
        <DialogHeader>
          <DialogTitle>Manage Hotels — {user?.username || "(user)"}</DialogTitle>
          <DialogDescription>
            One user can own several hotels. The primary one is what appears on the user card and on legacy reports.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-muted-foreground py-4">Loading…</p>}

        {!loading && (
          <div className="space-y-3">
            {hotels.length === 0 && <p className="text-sm text-muted-foreground py-3">No hotels yet.</p>}

            {hotels.map((h) => (
              <Card key={h.id} className={`border-2 ${h.is_primary ? "border-brand-navy" : "border-neutral-200"}`}>
                <CardContent className="p-3 space-y-2">
                  {editingId === h.id && editForm ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={editForm.hotel_name} onChange={(e) => setEditForm({ ...editForm, hotel_name: e.target.value })} placeholder="Hotel name" data-testid="hotels-edit-name" />
                        <Input value={editForm.hotel_code} onChange={(e) => setEditForm({ ...editForm, hotel_code: e.target.value })} placeholder="Hotel code (short)" />
                      </div>
                      <AddressAutocomplete
                        value={editForm.hotel_address}
                        onChange={(v) => setEditForm({ ...editForm, hotel_address: v })}
                        onPlaceSelected={(p) => setEditForm({ ...editForm, hotel_address: p.address, lat: p.lat, lng: p.lng, place_id: p.place_id })}
                        placeholder="Address (start typing to see suggestions)"
                        testId={`hotels-edit-address-${h.id}`}
                      />
                      {editForm.lat != null && editForm.lng != null && (
                        <p className="text-[11px] text-green-700">📍 Geocoded: {editForm.lat.toFixed(5)}, {editForm.lng.toFixed(5)}</p>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={editForm.suburb} onChange={(e) => setEditForm({ ...editForm, suburb: e.target.value })} placeholder="Suburb" />
                        <Input value={editForm.abn} onChange={(e) => setEditForm({ ...editForm, abn: e.target.value })} placeholder="ABN (synced to Xero)" data-testid={`hotels-edit-abn-${h.id}`} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={editForm.route_id} onValueChange={(v) => setEditForm({ ...editForm, route_id: v })}>
                          <SelectTrigger><SelectValue placeholder="Route" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— No route —</SelectItem>
                            {routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <OpeningHoursEditor
                        value={editForm.opening_hours}
                        onChange={(v) => setEditForm({ ...editForm, opening_hours: v })}
                      />
                      <div>
                        <label className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-1 block">
                          Special note (shown on run detail & delivery sheet)
                        </label>
                        <Textarea
                          value={editForm.special_note || ""}
                          onChange={(e) => setEditForm({ ...editForm, special_note: e.target.value })}
                          placeholder="e.g. Call reception before drop-off · Enter via rear loading dock"
                          rows={2}
                          className="text-sm"
                          data-testid={`hotels-edit-special-note-${h.id}`}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditForm(null); }}><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                        <Button size="sm" onClick={() => saveEdit(h)} data-testid={`hotels-save-${h.id}`}><Check className="w-3.5 h-3.5 mr-1" />Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold truncate">{h.hotel_name}</span>
                          {h.is_primary && (
                            <Badge className="bg-brand-navy/10 text-brand-navy border-brand-navy/30"><Star className="w-3 h-3 mr-1" />Primary</Badge>
                          )}
                          {h.hotel_code && <Badge variant="outline">{h.hotel_code}</Badge>}
                          {h.lat != null && h.lng != null && (
                            <Badge variant="outline" className="text-green-700 border-green-300 text-[10px]">📍 Geocoded</Badge>
                          )}
                          {h.opening_hours && Object.keys(h.opening_hours).length > 0 && (
                            <Badge variant="outline" className="text-blue-700 border-blue-300 text-[10px]">🕘 Hours set</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {h.hotel_address || "(no address)"}
                          {h.suburb ? ` · ${h.suburb}` : ""}
                          {h.abn ? ` · ABN ${h.abn}` : ""}
                          {h.route_id ? ` · ${routeName(h.route_id)}` : ""}
                        </p>
                        {h.special_note && (
                          <p className="mt-1 text-xs text-amber-800 bg-amber-50 border-l-2 border-amber-500 pl-2 py-1 rounded-r whitespace-pre-wrap" data-testid={`hotels-special-note-${h.id}`}>
                            <span className="font-semibold">Note:</span> {h.special_note}
                          </p>
                        )}
                        <p className="text-[10px] text-neutral-400 font-mono mt-1 truncate">ID: {h.id}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Billing:</span>
                          <Select value={h.payment_mode || "direct_debit"} onValueChange={(v) => setPaymentMode(h, v)}>
                            <SelectTrigger
                              className={`h-7 w-[210px] text-xs ${
                                (h.payment_mode || "direct_debit") === "manual_invoice"
                                  ? "border-amber-400 text-amber-800 bg-amber-50"
                                  : "border-emerald-300 text-emerald-800 bg-emerald-50"
                              }`}
                              data-testid={`hotels-payment-mode-${h.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="direct_debit">Direct Debit (card charge)</SelectItem>
                              <SelectItem value="manual_invoice">Manual Invoice (block charge)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        {onManageHotelProducts && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-700 border-purple-300 hover:bg-purple-50"
                            onClick={() => onManageHotelProducts(h)}
                            data-testid={`hotels-products-${h.id}`}
                            title="Configure products & pricing for this hotel"
                          >
                            <Package className="w-3.5 h-3.5 mr-1" />
                            Products
                          </Button>
                        )}
                        {!h.is_primary && (
                          <Button size="icon" variant="outline" onClick={() => setPrimary(h)} title="Set as primary" data-testid={`hotels-set-primary-${h.id}`}>
                            <Star className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="icon" variant="outline" onClick={() => startEdit(h)} data-testid={`hotels-edit-${h.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <span
                          className={
                            "inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider border " +
                            (h.has_card
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "bg-neutral-100 text-neutral-500 border-neutral-300")
                          }
                          data-testid={`hotels-card-status-${h.id}`}
                          title={h.has_card
                            ? "Customer has a saved card on file"
                            : "Customer has not saved a card yet — they add one from their Settings tab"}
                        >
                          <CreditCard className="w-3 h-3" />
                          {h.has_card ? "Card on file" : "No card"}
                        </span>
                        <Button size="icon" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => remove(h)} disabled={hotels.length === 1} data-testid={`hotels-delete-${h.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Add new hotel */}
            {adding ? (
              <Card className="border-2 border-dashed border-brand-navy">
                <CardContent className="p-3 space-y-2">
                  <p className="font-semibold text-sm text-brand-navy">Add hotel</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newHotel.hotel_name} onChange={(e) => setNewHotel({ ...newHotel, hotel_name: e.target.value })} placeholder="Hotel name *" data-testid="hotels-new-name" />
                    <Input value={newHotel.hotel_code} onChange={(e) => setNewHotel({ ...newHotel, hotel_code: e.target.value })} placeholder="Hotel code" />
                  </div>
                  <AddressAutocomplete
                    value={newHotel.hotel_address}
                    onChange={(v) => setNewHotel({ ...newHotel, hotel_address: v })}
                    onPlaceSelected={(p) => setNewHotel({ ...newHotel, hotel_address: p.address, lat: p.lat, lng: p.lng, place_id: p.place_id })}
                    placeholder="Address (start typing to see suggestions)"
                    testId="hotels-new-address"
                  />
                  {newHotel.lat != null && newHotel.lng != null && (
                    <p className="text-[11px] text-green-700">📍 Geocoded: {newHotel.lat.toFixed(5)}, {newHotel.lng.toFixed(5)}</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newHotel.suburb} onChange={(e) => setNewHotel({ ...newHotel, suburb: e.target.value })} placeholder="Suburb" />
                    <Input value={newHotel.abn} onChange={(e) => setNewHotel({ ...newHotel, abn: e.target.value })} placeholder="ABN (synced to Xero)" data-testid="hotels-new-abn" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={newHotel.route_id} onValueChange={(v) => setNewHotel({ ...newHotel, route_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Route" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No route —</SelectItem>
                        {routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <OpeningHoursEditor
                    value={newHotel.opening_hours}
                    onChange={(v) => setNewHotel({ ...newHotel, opening_hours: v })}
                  />
                  <div>
                    <label className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-1 block">
                      Special note (shown on run detail & delivery sheet)
                    </label>
                    <Textarea
                      value={newHotel.special_note || ""}
                      onChange={(e) => setNewHotel({ ...newHotel, special_note: e.target.value })}
                      placeholder="e.g. Call reception before drop-off · Enter via rear loading dock"
                      rows={2}
                      className="text-sm"
                      data-testid="hotels-new-special-note"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAdding(false)}><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                    <Button size="sm" onClick={handleAdd} data-testid="hotels-add-confirm"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button variant="outline" onClick={() => setAdding(true)} className="w-full border-dashed" data-testid="hotels-add-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add hotel
              </Button>
            )}
          </div>
        )}

      </DialogContent>
    </Dialog>
  );
}
